import React, { useMemo, useState } from 'react';
import {
  LuAlertTriangle,
  LuBoxes,
  LuCircleDollarSign,
  LuPackage,
  LuSearch,
  LuX,
} from 'react-icons/lu';
import { useProducts } from '../ProductContext';
import {
  factoryCategoryShort,
  isFactoryRawMaterialCategory,
  resolveFactoryMaterialUnit,
} from '../constants/factoryStockCategories';
import {
  formatFactoryQtyWithUnit,
  formatFactoryStockQty,
  productUnitLabel,
  unitPerLabel,
} from '../constants/productUnits';
import { isLowStock } from '../utils/lowStock';
import './FactoryStockOverviewPage.css';

function stockStatus(item) {
  const qty = Number(item.quantity) || 0;
  if (qty === 0) return { key: 'out', label: 'Out of stock' };
  if (isLowStock(item)) return { key: 'low', label: 'Low stock' };
  return { key: 'ok', label: 'In stock' };
}

export default function FactoryStockOverviewPage({ onNavigate }) {
  const { products, inventoryLoading } = useProducts();
  const [search, setSearch] = useState('');

  const go = (key) => {
    if (typeof onNavigate === 'function') onNavigate(key);
  };

  const stockItems = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    return list
      .filter((p) => isFactoryRawMaterialCategory(p.category))
      .sort((a, b) => {
        const c = String(a.category || '').localeCompare(String(b.category || ''));
        return c !== 0 ? c : String(a.name).localeCompare(String(b.name));
      });
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stockItems;
    return stockItems.filter((p) => {
      const hay = [p.name, p.category, String(p.quantity ?? '')].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [stockItems, search]);

  const stats = useMemo(() => {
    const withStock = stockItems.filter((p) => (Number(p.quantity) || 0) > 0);
    const low = stockItems.filter(isLowStock);
    const totalUnits = stockItems.reduce(
      (s, p) => s + Math.round(Number(p.quantity) || 0),
      0
    );
    const value = stockItems.reduce((s, p) => {
      const qty = Number(p.quantity) || 0;
      const cost = Number(p.purchasingPrice) || 0;
      return s + qty * cost;
    }, 0);
    return {
      itemCount: stockItems.length,
      inStockCount: withStock.length,
      totalUnits,
      lowCount: low.length,
      stockValue: value,
    };
  }, [stockItems]);

  const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString() : '—');
  const fmtMoney = (n) => (Number.isFinite(Number(n)) && Number(n) > 0 ? fmt(n) : '—');

  if (inventoryLoading) {
    return (
      <section className="factory-stock page-bleed">
        <p className="factory-stock__muted">Loading stock…</p>
      </section>
    );
  }

  return (
    <section className="factory-stock page-bleed">
      <header className="factory-stock__hero">
        <div className="factory-stock__hero-icon" aria-hidden>
          <LuBoxes size={22} />
        </div>
        <div>
          <h1 className="factory-stock__title">Raw materials stock</h1>
          <p className="factory-stock__sub">
            On-hand quantities for cement, sand, fuel, and other inputs. Stock increases when you{' '}
            <strong>receive</strong> a delivery in Tracking status. Finished bricks and tiles are not shown here.
          </p>
        </div>
      </header>

      <div className="factory-stock__stats">
        <article className="factory-stock__stat factory-stock__stat--teal">
          <span className="factory-stock__stat-icon" aria-hidden>
            <LuPackage size={18} />
          </span>
          <div>
            <span className="factory-stock__stat-label">Materials in stock</span>
            <strong className="factory-stock__stat-value">
              {stats.inStockCount}
              <span className="factory-stock__stat-of"> / {stats.itemCount}</span>
            </strong>
          </div>
        </article>
        <article className="factory-stock__stat factory-stock__stat--indigo">
          <span className="factory-stock__stat-icon" aria-hidden>
            <LuBoxes size={18} />
          </span>
          <div>
            <span className="factory-stock__stat-label">Total units on hand</span>
            <strong className="factory-stock__stat-value">{formatFactoryStockQty(stats.totalUnits)}</strong>
          </div>
        </article>
        <article className="factory-stock__stat factory-stock__stat--warn">
          <span className="factory-stock__stat-icon" aria-hidden>
            <LuAlertTriangle size={18} />
          </span>
          <div>
            <span className="factory-stock__stat-label">Low stock</span>
            <strong className="factory-stock__stat-value">{stats.lowCount}</strong>
          </div>
        </article>
        <article className="factory-stock__stat factory-stock__stat--emerald">
          <span className="factory-stock__stat-icon" aria-hidden>
            <LuCircleDollarSign size={18} />
          </span>
          <div>
            <span className="factory-stock__stat-label">Stock value (cost)</span>
            <strong className="factory-stock__stat-value">TZS {fmt(stats.stockValue)}</strong>
          </div>
        </article>
      </div>

      <div className="factory-stock__toolbar">
        <div className="factory-stock__search-wrap">
          <LuSearch className="factory-stock__search-icon" size={16} aria-hidden />
          <input
            type="search"
            className="factory-stock__search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search material, category, quantity…"
            aria-label="Search raw materials stock"
          />
        </div>
        {search.trim() ? (
          <button type="button" className="factory-stock__clear" onClick={() => setSearch('')}>
            <LuX size={14} aria-hidden /> Clear
          </button>
        ) : null}
      </div>

      <p className="factory-stock__count">
        {filtered.length} of {stockItems.length} raw material{stockItems.length !== 1 ? 's' : ''}
        {stats.lowCount > 0 ? (
          <>
            {' '}
            ·{' '}
            <button type="button" className="factory-stock__inline-link" onClick={() => go('stock-alerts')}>
              {stats.lowCount} low stock alert{stats.lowCount !== 1 ? 's' : ''}
            </button>
          </>
        ) : null}
      </p>

      {stockItems.length === 0 ? (
        <div className="factory-stock__empty">
          <LuPackage size={36} aria-hidden />
          <p>No raw materials registered yet.</p>
          <p className="factory-stock__muted">Add materials under Raw materials, then receive deliveries to build stock.</p>
          <button type="button" className="factory-stock__btn" onClick={() => go('add-raw-material')}>
            Add materials
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <p className="factory-stock__muted">No materials match your search.</p>
      ) : (
        <div className="factory-stock__grid">
          {filtered.map((p) => {
            const st = stockStatus(p);
            const materialUnit = resolveFactoryMaterialUnit(p);
            const cost = Number(p.purchasingPrice) || 0;
            const qty = Number(p.quantity) || 0;
            const lineValue = qty * cost;
            return (
              <article
                key={p.id}
                className={`factory-stock__card factory-stock__card--${st.key}`}
              >
                <div className="factory-stock__card-top">
                  <span className={`factory-stock__badge factory-stock__badge--${st.key}`}>
                    {st.key === 'low' ? <LuAlertTriangle size={12} aria-hidden /> : null}
                    {st.label}
                  </span>
                  <span className="factory-stock__card-cat" title={p.category}>
                    {factoryCategoryShort(p.category)}
                  </span>
                </div>
                <h2 className="factory-stock__card-name">{p.name}</h2>
                <div className="factory-stock__card-qty-wrap">
                  <span className="factory-stock__card-qty-label">On hand</span>
                  <div className={`factory-stock__card-qty-row factory-stock__card-qty-row--${st.key}`}>
                    <span className="factory-stock__card-qty">{formatFactoryStockQty(p.quantity)}</span>
                    <span className="factory-stock__card-unit">{productUnitLabel(materialUnit)}</span>
                  </div>
                </div>
                <dl className="factory-stock__card-meta">
                  <div>
                    <dt>Purchase price</dt>
                    <dd>
                      TZS {fmtMoney(p.purchasingPrice)}
                      {Number(p.purchasingPrice) > 0 ? (
                        <span className="factory-stock__card-per"> {unitPerLabel(materialUnit)}</span>
                      ) : null}
                    </dd>
                  </div>
                  <div>
                    <dt>Min level</dt>
                    <dd>
                      {p.lowStockLimit > 0
                        ? formatFactoryQtyWithUnit(p.lowStockLimit, materialUnit)
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>Line value</dt>
                    <dd>{lineValue > 0 ? `TZS ${fmt(lineValue)}` : '—'}</dd>
                  </div>
                </dl>
              </article>
            );
          })}
        </div>
      )}

      <div className="factory-stock__actions">
        <button type="button" className="factory-stock__action factory-stock__action--primary" onClick={() => go('orders-tracking')}>
          Receive delivery
        </button>
        <button type="button" className="factory-stock__action" onClick={() => go('orders-make')}>
          Order materials
        </button>
      </div>
    </section>
  );
}
